import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';

type Photo = {
  url: string;
};

type Unit = {
  modelId: string;
  name: string;
  availabilityDate: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  isNewUnit?: boolean;
  createdAgo?: string;
  priceDrop?: number;
};

export type Floorplan = {
  modelId: string;
  name: string;
  type: string;
  availabilityDate: string;
  beds: number;
  baths: number;
  sqft: number;
  low: number;
  high: number;
  priceRangeDisplay: Array<string>;
  photos: Array<Photo>;
  largePhotos: Array<Photo>;
  thumbPhotos: Array<Photo>;
  layoutCount?: number;
  units: Array<Unit>;
  zillow3dTourUrl: string;
};

export type BedroomOption = {
  beds: number;
  label: string;
};

const getBedroomLabel = (beds: number): string => {
  if (beds === 0) return 'Studio';
  if (beds === 1) return '1 Bed';
  return `${beds} Beds`;
};

export const useFloorplansByBedroom = () => {
  const floorplans = useSelector(
    (state: RootReduxState) => state.currentListingDetails?.currentListing?.floorplans as Array<Floorplan> | undefined,
  );

  const { bedroomOptions, floorplansByBedroom } = useMemo(() => {
    if (!floorplans || floorplans.length === 0) {
      return { bedroomOptions: [] as Array<BedroomOption>, floorplansByBedroom: new Map<number, Array<Floorplan>>() };
    }

    const grouped = new Map<number, Array<Floorplan>>();

    for (const fp of floorplans) {
      const existing = grouped.get(fp.beds) ?? [];
      existing.push(fp);
      grouped.set(fp.beds, existing);
    }

    const sortedBeds = Array.from(grouped.keys()).sort((a, b) => a - b);
    const options: Array<BedroomOption> = sortedBeds.map((beds) => ({
      beds,
      label: getBedroomLabel(beds),
    }));

    return { bedroomOptions: options, floorplansByBedroom: grouped };
  }, [floorplans]);

  return {
    bedroomOptions,
    floorplansByBedroom,
    hasFloorplans: bedroomOptions.length > 0,
  };
};
